import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, Spinner, Text, Tab, TabList, Field, Input, Textarea,
  Dropdown, Option, Switch, Checkbox, Label,
} from "@fluentui/react-components";
import { Save20Regular, Dismiss20Regular, Delete20Regular, Add20Filled, ChevronRight20Regular, ChevronDown20Regular } from "@fluentui/react-icons";
import { GetAgentAI, AddAgentAI, UpdateAgentAI } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useMsal } from "@azure/msal-react";
import SearchResourceInGraph from "../GenericComponent/SearchResourceInGraph";
import "../CSS/AgentAIComponent.css";

// Voix Realtime API (TTS)
const VOICE_OPTIONS = ["marin", "cedar", "alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse"];
const PERSONALITY_OPTIONS = [
  { value: "friendly-casual", label: "Friendly & Casual" },
  { value: "friendly-professional", label: "Friendly & Professional" },
  { value: "formal", label: "Formal" },
];
const QUALIFICATION_STRATEGIES = [
  { value: "conversational", label: "Conversational (infer intent from the dialogue)" },
  { value: "keyword", label: "Keyword (match on keywords only)" },
  { value: "hybrid", label: "Hybrid (keywords + conversation)" },
];
const REGION_OPTIONS = [
  { value: "EU", label: "Europe (EU)" },
  { value: "US", label: "United States (US)" },
  { value: "APAC", label: "Asia-Pacific (APAC)" },
];
const LOG_LEVEL_OPTIONS = ["debug", "info", "warn", "error"];

// Squelette complet d'un agent IA (aligné sur le doc Cosmos iaAgent).
const emptyAgentAI = () => ({
  displayName: "",
  scope: "",
  type: "iaAgent",
  enabled: true,
  agentPersonality: { name: "", role: "", company: "", personality: "friendly-casual", languageStyle: "formal-polite", voice: "marin" },
  behaviorRules: {
    general: [],
    greeting: { mandatory: true, format: "Hello, I'm {name}, {role} at {company}! How can I help you today?", includeAllElements: true },
    languageAdaptation: {
      enabled: true,
      detectionProtocol: [
        "1. Detect the language from the first words",
        "2. Switch immediately to that language",
        "3. Rephrase the user's question to confirm",
        "4. Continue the whole conversation in that language",
        "5. Never mix multiple languages",
      ],
    },
    responseFormat: { maxSentences: 2, avoidRepetition: true, confirmBeforeAction: true },
  },
  skills: {
    userSearch: { enabled: true, tool: "search_user", description: "Search for a user in the Microsoft Teams directory", requireConfirmation: false, autoExecute: true },
    userPresence: { enabled: true, tool: "get_user_presence", description: "Check a user's availability", requireConfirmation: false, autoExecute: true, announceStatus: true },
    transferToUser: { enabled: true, tool: "transfer_to_user", description: "Transfer the call to a Microsoft Teams user", requireConfirmation: true },
    transferToPhone: { enabled: true, tool: "transfer_to_phone", description: "Transfer to an external phone number", requireConfirmation: true },
    hangupCall: { enabled: true, tool: "hangup_call", description: "End the call", requireConfirmation: false, farewellMessage: "Goodbye and have a great day!", mandatory: true },
    leaveMessage: { enabled: true, tool: "leave_message", description: "Take a message for an absent user", requireConfirmation: false, captureInfo: ["caller_name", "phone_number", "message"] },
    // Connecteurs personnalisés (Custom CRM / API) : ajoutés à la demande sous forme de skills
    // portant un bloc dataQuery. Le runtime (repo WebSocket, DataQueryService) prend la 1re skill
    // activée en lecture et injecte le résultat mappé au décroché. Clés de "mapping" → variables $nom$.
    callQualification: {
      enabled: true, tool: "escalate_to_agent",
      strategy: "conversational", clarifyBeforeRouting: true, maxClarifyingQuestions: 1,
      routes: [],
      fallback: { behavior: "ask_clarification", disambiguationMessage: { en: "", fr: "" } },
      rules: [
        "Identify the caller's intent from their FIRST message when possible",
        "If intent is clearly support OR sales → announce in the caller's language then transfer immediately",
        "If intent is ambiguous → ask the clarifying question ONCE",
        "If still unclear after one clarification → transfer to the fallback queue",
        "NEVER mention the queueId to the caller — use the queue name only",
        "ALWAYS announce the queue in the caller's language BEFORE calling escalate_to_agent",
      ],
    },
  },
  knowledgeBase: { enabled: false, name: "", language: "en", type: "inline", data: [], usageInstructions: "", fallbackMessage: "" },
  securityRules: [],
  errorHandling: { maxRetries: 3, fallbackToHuman: true, fallbackQueueId: "", errorMessages: { userNotFound: "", transferFailed: "", technicalError: "", timeout: "" } },
  // Configuration globale générique
  region: "EU",
  dataLocation: "Europe",
  timeZone: "Europe/Amsterdam",
  analytics: { trackConversations: true, trackToolsUsage: true, trackLanguageDetection: true, trackQualificationRoute: true, trackSentiment: false, logLevel: "info" },
  advanced: { delays: { before_transfer_ms: 3000, before_hangup_ms: 2500, audio_commit_ms: 100, audio_delay_barge_in_ms: 40 } },
});

// Met à jour une valeur à un chemin imbriqué de façon immuable.
const setIn = (obj, path, value) => {
  const keys = Array.isArray(path) ? path : path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
};

// Parse un texte en objet JSON (rejette tableaux/primitifs, comme le body des workflows).
const parseJsonObject = (text) => {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return { valid: true, parsed };
    return { valid: false };
  } catch { return { valid: false }; }
};

const AgentAIFormComponent = ({ ACTIVE_TENANT, st_AgentAIs, setAgentAIs, CALL_FLOWS, SCOPES_NAMES, QUEUES }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agentAIId } = useParams();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const isCreateMode = agentAIId === "new";
  const isEditMode = !isCreateMode;
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [agentAI, setAgentAIState] = useState(emptyAgentAI());
  const [selectedTab, setSelectedTab] = useState("identity");
  const [loadFailed, setLoadFailed] = useState(false);
  const [newRule, setNewRule] = useState("");
  const [newSecurityRule, setNewSecurityRule] = useState("");
  const [expandedRoutes, setExpandedRoutes] = useState({}); // routes repliées par défaut
  const [openSkill, setOpenSkill] = useState(null); // accordéon skills : une seule ouverte, tout replié par défaut
  const [bodyText, setBodyText] = useState({});  // texte brut du body JSON par connecteur (édition libre)
  const [bodyError, setBodyError] = useState({}); // état d'erreur JSON par connecteur
  const [advOpen, setAdvOpen] = useState({}); // repli "Advanced" (allowlist SSRF) par connecteur
  const [sendBody, setSendBody] = useState({}); // toggle "envoyer un body JSON custom" par connecteur
  const [kbModeOverride, setKbModeOverride] = useState(null); // format KB choisi dans l'UI (qa/markdown/url) — indépendant de rawText

  // Options de queues (sélection par NOM, id stocké derrière).
  const queueOptions = React.useMemo(
    () => Object.values(QUEUES || {})
      .map((q) => ({ id: q.id, name: q.queueName || q.id }))
      .filter((q) => q.id),
    [QUEUES]
  );
  const queueNameById = React.useMemo(() => {
    const m = {};
    queueOptions.forEach((q) => { m[q.id] = q.name; });
    return m;
  }, [queueOptions]);

  const set = (path, value) => setAgentAIState((prev) => setIn(prev, path, value));

  // Éditeur de liste de chaînes réutilisable (édition en place + ajout d'une ligne vide).
  const renderListEditor = (items, onChange, placeholder) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
      {(items || []).map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input style={{ flex: 1 }} value={it} placeholder={placeholder}
            onChange={(e, d) => onChange((items || []).map((x, j) => (j === i ? d.value : x)))} />
          <Button appearance="subtle" icon={<Delete20Regular />}
            onClick={() => onChange((items || []).filter((_, j) => j !== i))} />
        </div>
      ))}
      <div>
        <Button appearance="primary" icon={<Add20Filled />}
          onClick={() => onChange([...(items || []), ""])}>
          {t("ADD", { defaultValue: "Add" })}
        </Button>
      </div>
    </div>
  );

  // Éditeur d'objet clé/valeur (headers, mapping). La source de vérité reste l'objet ;
  // on l'édite via ses entrées. L'ajout génère une clé unique non vide pour éviter les
  // collisions (Object.fromEntries écraserait des clés dupliquées).
  const renderKvEditor = (obj, onChange, keyPh, valPh, base) => {
    const entries = Object.entries(obj || {});
    const commit = (next) => onChange(Object.fromEntries(next));
    const addRow = () => {
      const o = obj || {};
      let key = base || "key", n = 1;
      while (Object.prototype.hasOwnProperty.call(o, key)) key = `${base || "key"}${n++}`;
      onChange({ ...o, [key]: "" });
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
        {entries.map(([k, v], i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Input style={{ flex: 1 }} value={k} placeholder={keyPh}
              onChange={(e, d) => commit(entries.map((en, j) => (j === i ? [d.value, en[1]] : en)))} />
            <Input style={{ flex: 2 }} value={v} placeholder={valPh}
              onChange={(e, d) => commit(entries.map((en, j) => (j === i ? [en[0], d.value] : en)))} />
            <Button appearance="subtle" icon={<Delete20Regular />}
              onClick={() => commit(entries.filter((_, j) => j !== i))} />
          </div>
        ))}
        <div>
          <Button appearance="primary" icon={<Add20Filled />} onClick={addRow}>
            {t("ADD", { defaultValue: "Add" })}
          </Button>
        </div>
      </div>
    );
  };

  // Édition libre du body JSON d'un connecteur : on garde le texte brut en state (pour tolérer
  // un JSON temporairement invalide) et on n'écrit l'OBJET parsé dans le modèle que s'il est valide.
  const handleBodyChange = (key, value) => {
    setBodyText((m) => ({ ...m, [key]: value }));
    if (!value.trim()) {
      setBodyError((m) => ({ ...m, [key]: false }));
      set(`skills.${key}.dataQuery.body`, {});
      return;
    }
    const r = parseJsonObject(value);
    if (r.valid) {
      setBodyError((m) => ({ ...m, [key]: false }));
      set(`skills.${key}.dataQuery.body`, r.parsed);
    } else {
      setBodyError((m) => ({ ...m, [key]: true }));
    }
  };

  useEffect(() => {
    if (isCreateMode) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        // La liste a déjà chargé les docs complets dans le store.
        const fromStore = st_AgentAIs?.find((ai) => ai.id === agentAIId);
        if (fromStore) {
          if (!cancelled) setAgentAIState({ ...emptyAgentAI(), ...fromStore });
        } else {
          const token = await getAccessToken(instance, activeAccount);
          // scope inconnu hors store : on tente sur 'default' (fallback)
          const res = await GetAgentAI(ACTIVE_TENANT, userId, agentAIId, "default", token);
          if (res && (res.id || res.displayName)) {
            if (!cancelled) setAgentAIState({ ...emptyAgentAI(), ...res });
          } else if (!cancelled) setLoadFailed(true);
        }
      } catch (e) {
        console.error("❌ Error loading Agent AI:", e);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentAIId]);

  const handleSave = async () => {
    if (!agentAI.agentPersonality?.name || !agentAI.scope) {
      alert(t("AI_AGENT_REQUIRED_FIELDS", { defaultValue: "Please fill the required fields (Agent Name, Scope)." }));
      return;
    }
    setSaving(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      const scope = agentAI.scope;
      if (isCreateMode) {
        const res = await AddAgentAI(ACTIVE_TENANT, userId, scope, token, agentAI);
        const saved = (res && (res.data || res.agentAI)) || agentAI;
        setAgentAIs([...(st_AgentAIs || []), saved]);
        navigate("/iaagent"); // création : retour à la liste
      } else {
        const res = await UpdateAgentAI(ACTIVE_TENANT, userId, agentAI.id, scope, token, agentAI);
        const saved = (res && (res.data || res.agentAI)) || agentAI;
        setAgentAIs((st_AgentAIs || []).map((ai) => (ai.id === agentAI.id ? saved : ai)));
        // update : on RESTE sur la même URL (pas de navigate).
      }
    } catch (e) {
      console.error("❌ Error saving Agent AI:", e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="agentai-loading-container"><Spinner size="large" label={t("LOADING")} /></div>;
  }
  if (loadFailed) {
    return (
      <div className="agentai-error-container">
        <Text weight="semibold">{t("AGENT_AI_NOT_FOUND", { defaultValue: "Agent AI not found" })}</Text>
        <Button onClick={() => navigate("/iaagent")}>{t("BACK_TO_LIST", { defaultValue: "Back" })}</Button>
      </div>
    );
  }

  const ap = agentAI.agentPersonality || {};
  const br = agentAI.behaviorRules || {};
  const la = br.languageAdaptation || {};
  const sk = agentAI.skills || {};
  const cq = sk.callQualification || {};
  const kb = agentAI.knowledgeBase || {};
  const eh = agentAI.errorHandling || {};

  const greetingPreview = (br.greeting?.format || "")
    .replace("{name}", ap.name || "")
    .replace("{role}", ap.role || "")
    .replace("{company}", ap.company || "");

  // ---- Onglets ----
  const TABS = [
    { value: "identity", label: t("TAB_IDENTITY", { defaultValue: "Identity" }) },
    { value: "voice", label: t("TAB_VOICE", { defaultValue: "Voice & Languages" }) },
    { value: "personality", label: t("TAB_PERSONALITY", { defaultValue: "Personality" }) },
    { value: "greeting", label: t("TAB_GREETING", { defaultValue: "Greeting & Behavior" }) },
    { value: "skills", label: t("TAB_SKILLS", { defaultValue: "Skills" }) },
    { value: "routing", label: t("TAB_ROUTING", { defaultValue: "Routing" }) },
    { value: "knowledge", label: t("TAB_KNOWLEDGE", { defaultValue: "Knowledge Base" }) },
    { value: "security", label: t("TAB_SECURITY", { defaultValue: "Security & Errors" }) },
    { value: "advanced", label: t("TAB_ADVANCED", { defaultValue: "Advanced" }) },
  ];

  const renderIdentity = () => (
    <div className="agentai-form-section">
      {isCreateMode ? (
        <>
          {/* Recherche du resource account dans Graph (même mécanisme que la création
              d'un Call flow). L'id du resource account sélectionné devient l'id de l'agent IA. */}
          <Field label={t("RESOURCE_ACCOUNT", { defaultValue: "Resource account" })} required>
            <SearchResourceInGraph
              displayName={agentAI.displayName || ""}
              onSelectUser={(resource) => {
                if (resource?.id) {
                  set("id", resource.id);
                  set("displayName", resource.displayName || resource.id);
                  if (resource.userPrincipalName) set("userPrincipalName", resource.userPrincipalName);
                }
              }}
            />
          </Field>
          <Field label={t("RESOURCE_ACCOUNT_ID", { defaultValue: "Resource Account ID" })}>
            <Input value={agentAI.id || ""} placeholder="—" disabled />
          </Field>
          <Field label={t("SCOPE", { defaultValue: "Scope" })} required>
            <Dropdown value={agentAI.scope || ""} selectedOptions={agentAI.scope ? [agentAI.scope] : []}
              placeholder={t("SELECT_SCOPE", { defaultValue: "Select scope" })}
              onOptionSelect={(e, d) => set("scope", d.optionValue)}>
              {(SCOPES_NAMES || []).map((s) => <Option key={s} value={s}>{s}</Option>)}
            </Dropdown>
          </Field>
        </>
      ) : (
        <>
          <Field label={t("RESOURCE_ACCOUNT_ID", { defaultValue: "Resource Account ID" })}>
            <Input value={agentAI.id || ""} placeholder="—" disabled />
          </Field>
          <Field label={t("RESOURCE_ACCOUNT_NAME", { defaultValue: "Resource Account Name" })}>
            <Input value={agentAI.displayName || ""} placeholder="—" disabled />
          </Field>
        </>
      )}
      <Field label={t("AGENT_NAME", { defaultValue: "Agent Name" })} required>
        <Input value={ap.name || ""} onChange={(e, d) => set("agentPersonality.name", d.value)} />
      </Field>
      <Field label={t("ROLE", { defaultValue: "Role" })}>
        <Input value={ap.role || ""} onChange={(e, d) => set("agentPersonality.role", d.value)} />
      </Field>
      <Field label={t("COMPANY", { defaultValue: "Company" })}>
        <Input value={ap.company || ""} onChange={(e, d) => set("agentPersonality.company", d.value)} />
      </Field>
    </div>
  );

  const renderVoice = () => (
    <div className="agentai-form-section">
      <Field label={t("VOICE", { defaultValue: "Voice" })} required>
        <Dropdown
          value={ap.voice || ""}
          selectedOptions={ap.voice ? [ap.voice] : []}
          onOptionSelect={(e, d) => set("agentPersonality.voice", d.optionValue)}
        >
          {VOICE_OPTIONS.map((v) => (
            <Option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</Option>
          ))}
        </Dropdown>
      </Field>
      {/* Plus aucun réglage de langue ici. Le modèle Realtime traite ~57 langues quoi qu'on
          configure et verrouille de lui-même la langue de l'appelant dès sa première phrase
          (protocole language_lock, côté WebSocket). Les anciens champs — liste de langues
          supportées, puis langue par défaut — ne faisaient que brider ce comportement à partir
          d'une liste de 3 langues codée en dur. */}
      <Text size={200} style={{ color: "#605e5c" }}>
        {t("VOICE_HINT", { defaultValue: "The voice is used by the Realtime API for TTS. The agent detects the caller\u2019s language automatically and replies in it \u2014 no language configuration is needed." })}
      </Text>

      <Checkbox style={{ marginTop: 12 }}
        label={t("LANGUAGE_DETECTION_ENABLED", { defaultValue: "Automatic language detection enabled" })}
        checked={!!la.enabled} onChange={(e, d) => set("behaviorRules.languageAdaptation.enabled", d.checked)} />
      <Label weight="semibold" style={{ marginTop: 8 }}>
        {t("DETECTION_PROTOCOL", { defaultValue: "Language detection protocol (rules)" })}
      </Label>
      <Text size={200} style={{ color: "#605e5c", display: "block" }}>
        {t("DETECTION_PROTOCOL_HINT", { defaultValue: "Ordered steps the agent follows to detect and lock onto the caller's language." })}
      </Text>
      {renderListEditor(la.detectionProtocol,
        (next) => set("behaviorRules.languageAdaptation.detectionProtocol", next),
        t("DETECTION_STEP_PH", { defaultValue: "e.g. Detect the language from the first words" }))}
    </div>
  );

  const renderPersonality = () => (
    <div className="agentai-form-section">
      <Field label={t("PERSONALITY_TONE", { defaultValue: "Personality Tone" })} required>
        <Dropdown
          value={(PERSONALITY_OPTIONS.find((p) => p.value === ap.personality)?.label) || ap.personality || ""}
          selectedOptions={ap.personality ? [ap.personality] : []}
          onOptionSelect={(e, d) => set("agentPersonality.personality", d.optionValue)}
        >
          {PERSONALITY_OPTIONS.map((p) => <Option key={p.value} value={p.value}>{p.label}</Option>)}
        </Dropdown>
      </Field>
      <Field label={t("LANGUAGE_STYLE", { defaultValue: "Language Style" })}>
        <Input value={ap.languageStyle || ""} onChange={(e, d) => set("agentPersonality.languageStyle", d.value)} />
      </Field>
    </div>
  );

  const renderGreeting = () => (
    <div className="agentai-form-section">
      <Field label={t("GREETING_SENTENCE", { defaultValue: "Greeting Sentence" })} required
        hint={t("GREETING_PLACEHOLDERS", { defaultValue: "Available placeholders: {name}, {role}, {company}" })}>
        <Textarea value={br.greeting?.format || ""} rows={3}
          onChange={(e, d) => set("behaviorRules.greeting.format", d.value)} />
      </Field>
      <div className="agentai-preview-box">
        <Text weight="semibold">{t("PREVIEW", { defaultValue: "Preview" })}</Text>
        <Text italic style={{ display: "block" }}>{greetingPreview}</Text>
      </div>
      <Label weight="semibold" style={{ marginTop: 12 }}>{t("GENERAL_BEHAVIOR_RULES", { defaultValue: "General Behavior Rules" })}</Label>
      {(br.general || []).map((rule, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
          <Input style={{ flex: 1 }} value={rule}
            onChange={(e, d) => set("behaviorRules.general", (br.general || []).map((r, idx) => idx === i ? d.value : r))} />
          <Button appearance="subtle" icon={<Delete20Regular />}
            onClick={() => set("behaviorRules.general", (br.general || []).filter((_, idx) => idx !== i))} />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
        <Input style={{ flex: 1 }} value={newRule} placeholder={t("ADD_RULE", { defaultValue: "Add a rule (e.g. Never interrupt the user)" })}
          onChange={(e, d) => setNewRule(d.value)} />
        <Button appearance="primary" icon={<Add20Filled />} disabled={!newRule.trim()}
          onClick={() => { set("behaviorRules.general", [...(br.general || []), newRule.trim()]); setNewRule(""); }}>
          {t("ADD", { defaultValue: "Add" })}
        </Button>
      </div>

      <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
        {t("GREETING_RULES", { defaultValue: "Greeting rules" })}
      </Label>
      <Checkbox label={t("GREETING_MANDATORY", { defaultValue: "Greeting is mandatory at call start" })}
        checked={!!br.greeting?.mandatory} onChange={(e, d) => set("behaviorRules.greeting.mandatory", d.checked)} />
      <Checkbox label={t("GREETING_INCLUDE_ALL", { defaultValue: "Must include all elements (name, role, company)" })}
        checked={!!br.greeting?.includeAllElements} onChange={(e, d) => set("behaviorRules.greeting.includeAllElements", d.checked)} />

      <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
        {t("RESPONSE_FORMAT", { defaultValue: "Response format" })}
      </Label>
      <Field label={t("MAX_SENTENCES", { defaultValue: "Maximum sentences per reply" })}>
        <Input type="number" value={String(br.responseFormat?.maxSentences ?? 2)}
          onChange={(e, d) => set("behaviorRules.responseFormat.maxSentences", Number(d.value) || 0)} />
      </Field>
      <Checkbox label={t("AVOID_REPETITION", { defaultValue: "Avoid repeating what was already said" })}
        checked={!!br.responseFormat?.avoidRepetition} onChange={(e, d) => set("behaviorRules.responseFormat.avoidRepetition", d.checked)} />
      <Checkbox label={t("CONFIRM_BEFORE_ACTION", { defaultValue: "Confirm verbally before any action (transfer, hangup…)" })}
        checked={!!br.responseFormat?.confirmBeforeAction} onChange={(e, d) => set("behaviorRules.responseFormat.confirmBeforeAction", d.checked)} />
    </div>
  );

  const toggleSkill = (key) => setOpenSkill((cur) => (cur === key ? null : key));

  // Carte de skill repliable (accordéon : une seule ouverte à la fois, tout replié par défaut).
  const skillCard = (key, title, desc, body) => {
    const s = sk[key] || {};
    const open = openSkill === key;
    return (
      <div className="agentai-skill-card" key={key}>
        <div onClick={() => toggleSkill(key)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
          <div style={{ flex: 1 }}>
            <Text weight="semibold">{title}</Text>
            <Text size={200} style={{ display: "block", color: "#605e5c" }}>{desc}</Text>
          </div>
          <Text size={200} weight="semibold" style={{ whiteSpace: "nowrap", color: s.enabled ? "#0f7b0f" : "#a19f9d" }}>
            {s.enabled ? t("ENABLED", { defaultValue: "Enabled" }) : t("DISABLED", { defaultValue: "Disabled" })}
          </Text>
        </div>
        {open && (
          <div style={{ marginTop: 10 }}>
            <Switch checked={!!s.enabled} disabled={s.mandatory}
              label={t("ENABLED", { defaultValue: "Enabled" })}
              onChange={(e, d) => set(`skills.${key}.enabled`, d.checked)} />
            {body && body(s)}
          </div>
        )}
      </div>
    );
  };

  // Modes d'un connecteur personnalisé.
  const CONNECTOR_MODES = [
    { value: "read", label: t("MODE_READ", { defaultValue: "Read at answer (fetch context when the call starts)" }) },
    { value: "update", label: t("MODE_UPDATE", { defaultValue: "Update during/after call (send a request, e.g. update a ticket)" }) },
  ];

  // Supprime un connecteur (skill) du doc de façon immuable.
  const removeSkill = (key) => setAgentAIState((prev) => {
    const skills = { ...(prev.skills || {}) };
    delete skills[key];
    return { ...prev, skills };
  });

  // Ajoute un connecteur personnalisé URL-based, sur le même modèle que l'ancien crmLookup.
  const addConnector = () => {
    const key = `custom_${Date.now()}`;
    setAgentAIState((prev) => setIn(prev, `skills.${key}`, {
      enabled: false,
      label: t("NEW_CONNECTOR", { defaultValue: "New connector" }),
      mode: "read",
      dataQuery: { url: "", method: "POST", headers: {}, body: {}, mapping: {}, allowedHosts: [], timeoutSec: 10 },
      usageInstructions: [],
    }));
    setOpenSkill(key);
  };

  // Carte repliable d'un connecteur personnalisé (Custom CRM / API). Toute skill portant un
  // bloc dataQuery est un connecteur — y compris l'ancien "crmLookup" des docs existants.
  const renderConnectorCard = (key) => {
    const s = sk[key] || {};
    const dq = s.dataQuery || {};
    const open = openSkill === key;
    // hostname (SANS port) pour rester cohérent avec le check SSRF runtime (u.hostname).
    const urlHost = (() => { try { return new URL(dq.url).hostname; } catch { return ""; } })();
    const isHttps = !dq.url || /^https:\/\//i.test(dq.url);
    const allowedHosts = dq.allowedHosts || [];
    // Allowlist optionnelle : vide = aucune restriction (le runtime n'applique que HTTPS).
    // On n'avertit que si une allowlist EXISTE mais ne contient pas l'hôte de l'URL.
    const hostAllowed = !urlHost || allowedHosts.length === 0 || allowedHosts.includes(urlHost);
    const p = (suffix) => `skills.${key}.${suffix}`;
    const modeShort = s.mode === "update"
      ? t("MODE_UPDATE_SHORT", { defaultValue: "Update" })
      : t("MODE_READ_SHORT", { defaultValue: "Read at answer" });
    const subtitle = `${modeShort} · ${urlHost || t("NO_URL", { defaultValue: "no URL set" })}`;
    return (
      <div className="agentai-skill-card" key={key}>
        <div onClick={() => toggleSkill(key)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
          <div style={{ flex: 1 }}>
            <Text weight="semibold">{s.label || key}</Text>
            <Text size={200} style={{ display: "block", color: "#605e5c" }}>{subtitle}</Text>
          </div>
          <Text size={200} weight="semibold" style={{ whiteSpace: "nowrap", color: s.enabled ? "#0f7b0f" : "#a19f9d" }}>
            {s.enabled ? t("ENABLED", { defaultValue: "Enabled" }) : t("DISABLED", { defaultValue: "Disabled" })}
          </Text>
        </div>
        {open && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Field label={t("CONNECTOR_NAME", { defaultValue: "Name" })} style={{ flex: 1, minWidth: 180 }}>
                <Input value={s.label || ""} placeholder={t("NEW_CONNECTOR", { defaultValue: "New connector" })}
                  onChange={(e, d) => set(p("label"), d.value)} />
              </Field>
              <Field label={t("CONNECTOR_MODE", { defaultValue: "Mode" })} style={{ flex: 1, minWidth: 240 }}>
                <Dropdown value={(CONNECTOR_MODES.find((m) => m.value === (s.mode || "read"))?.label) || ""}
                  selectedOptions={[s.mode || "read"]}
                  onOptionSelect={(e, d) => set(p("mode"), d.optionValue)}>
                  {CONNECTOR_MODES.map((m) => <Option key={m.value} value={m.value} text={m.label}>{m.label}</Option>)}
                </Dropdown>
              </Field>
            </div>
            <Switch checked={!!s.enabled} label={t("ENABLED", { defaultValue: "Enabled" })}
              onChange={(e, d) => set(p("enabled"), d.checked)} />

            <div style={{ display: "flex", gap: 12 }}>
              <Field label={t("HTTP_METHOD", { defaultValue: "Method" })}>
                <Dropdown style={{ minWidth: 110 }} value={dq.method || "POST"} selectedOptions={[dq.method || "POST"]}
                  onOptionSelect={(e, d) => set(p("dataQuery.method"), d.optionValue)}>
                  {["GET", "POST", "PUT", "PATCH"].map((m) => <Option key={m} value={m}>{m}</Option>)}
                </Dropdown>
              </Field>
              <Field label={t("TIMEOUT_SEC", { defaultValue: "Timeout (s)" })}>
                <Input type="number" style={{ maxWidth: 100 }} value={String(dq.timeoutSec ?? 10)}
                  onChange={(e, d) => set(p("dataQuery.timeoutSec"), Number(d.value) || 0)} />
              </Field>
            </div>

            <Field
              label={t("REQUEST_URL", { defaultValue: "Request URL" })}
              validationState={!isHttps ? "error" : !hostAllowed ? "warning" : "none"}
              validationMessage={
                !isHttps
                  ? t("URL_MUST_BE_HTTPS", { defaultValue: "URL must start with https:// (the runtime rejects anything else)." })
                  : !hostAllowed
                    ? t("HOST_NOT_ALLOWED", { host: urlHost, defaultValue: `Add "${urlHost}" to Allowed hosts below, or the runtime will block the request.` })
                    : undefined
              }>
              <Textarea resize="vertical" value={dq.url || ""}
                placeholder="https://api.example.com/lookup?phoneNumber=$callerIdDigits$"
                onChange={(e, d) => set(p("dataQuery.url"), d.value)} />
            </Field>

            <div>
              <Label weight="semibold">{t("HTTP_HEADERS", { defaultValue: "Headers" })}</Label>
              {renderKvEditor(dq.headers, (v) => set(p("dataQuery.headers"), v),
                t("HEADER_NAME", { defaultValue: "Header" }),
                t("HEADER_VALUE", { defaultValue: "Value" }), "header")}
            </div>

            {(dq.method || "POST") !== "GET" && (() => {
              // Toggle "envoyer un body JSON custom" (défaut ON si un body existe déjà).
              const bodyOn = sendBody[key] !== undefined ? sendBody[key] : !!(dq.body && Object.keys(dq.body).length);
              return (
                <div>
                  <Switch checked={bodyOn}
                    label={t("SEND_CUSTOM_BODY", { defaultValue: "Send a custom JSON body" })}
                    onChange={(e, d) => {
                      setSendBody((m) => ({ ...m, [key]: d.checked }));
                      if (!d.checked) {
                        set(p("dataQuery.body"), {});
                        setBodyText((m) => ({ ...m, [key]: "" }));
                        setBodyError((m) => ({ ...m, [key]: false }));
                      }
                    }} />
                  {bodyOn && (
                    <Field style={{ marginTop: 6 }}
                      label={t("REQUEST_BODY", { defaultValue: "Request body (free JSON)" })}
                      validationState={bodyError[key] ? "error" : "none"}
                      validationMessage={bodyError[key] ? t("INVALID_JSON", { defaultValue: "Invalid JSON" }) : undefined}>
                      <Textarea rows={8} resize="vertical"
                        value={bodyText[key] !== undefined
                          ? bodyText[key]
                          : (dq.body && Object.keys(dq.body).length ? JSON.stringify(dq.body, null, 2) : "")}
                        placeholder={'{\n  "agent": "$AGENT_NAME$",\n  "customer": "$CALLER_ID_E164$",\n  "duration": "$CALL_HANDLE_TIME$",\n  "summary": "$CALL_SUMMARY$",\n  "transcriptUrl": "$TRANSCRIPT_FILE_URL$"\n}'}
                        onChange={(e, d) => handleBodyChange(key, d.value)} />
                    </Field>
                  )}
                </div>
              );
            })()}

            {(() => {
              // "Advanced" replié par défaut ; déplié auto si une allowlist est déjà définie.
              const open = advOpen[key] || allowedHosts.length > 0;
              return (
                <div>
                  <div onClick={() => setAdvOpen((m) => ({ ...m, [key]: !open }))}
                    style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                    <Text weight="semibold" size={200}>{t("ADVANCED", { defaultValue: "Advanced" })}</Text>
                  </div>
                  {open && (
                    <div style={{ marginTop: 6 }}>
                      <Label weight="semibold">{t("ALLOWED_HOSTS", { defaultValue: "Allowed hosts (optional — SSRF allowlist)" })}</Label>
                      {urlHost && !hostAllowed && (
                        <div>
                          <Button appearance="primary" icon={<Add20Filled />} style={{ marginTop: 4 }}
                            onClick={() => set(p("dataQuery.allowedHosts"), [...(dq.allowedHosts || []), urlHost])}>
                            {t("ADD_URL_HOST", { host: urlHost, defaultValue: `Add ${urlHost}` })}
                          </Button>
                        </div>
                      )}
                      {renderListEditor(dq.allowedHosts, (v) => set(p("dataQuery.allowedHosts"), v),
                        t("HOST_PLACEHOLDER", { defaultValue: "api.example.com" }))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div>
              <Label weight="semibold">{t("RESPONSE_MAPPING", { defaultValue: "Response mapping" })}</Label>
              <Text size={200} style={{ display: "block", color: "#605e5c" }}>
                {t("RESPONSE_MAPPING_DESC", { defaultValue: "variable ← JSON path (dot notation, array index allowed e.g. Cases.0.Status). Each variable becomes usable as $variable$." })}
              </Text>
              {renderKvEditor(dq.mapping, (v) => set(p("dataQuery.mapping"), v),
                t("VARIABLE_NAME", { defaultValue: "variable" }),
                t("JSON_PATH", { defaultValue: "path.to.field" }), "variable")}
            </div>

            {(s.mode || "read") === "read" && (
              <div>
                <Field
                  label={t("INJECT_TEMPLATE", { defaultValue: "Context injection template (optional)" })}>
                  <Textarea resize="vertical" rows={3}
                    value={dq.injectTemplate || ""}
                    placeholder={"CONTEXTE CLIENT — Nom: $clientName$ | Dernier ticket: $lastCaseSubject$ ($lastCaseStatus$)"}
                    onChange={(e, d) => set(p("dataQuery.injectTemplate"), d.value)} />
                </Field>
                <Text size={200} style={{ display: "block", color: "#605e5c", marginTop: 4 }}>
                  {t("INJECT_TEMPLATE_DESC", { defaultValue: "How the fetched data is written into the agent's context. Uses your mapped $variables$. If left empty, the runtime auto-lists every mapped field." })}
                </Text>
              </div>
            )}

            <div>
              <Label weight="semibold">{t("USAGE_INSTRUCTIONS", { defaultValue: "Usage instructions" })}</Label>
              <Text size={200} style={{ display: "block", color: "#605e5c" }}>
                {t("USAGE_INSTRUCTIONS_DESC", { defaultValue: "Prompt guidance telling the agent how to use the mapped $variables$." })}
              </Text>
              {renderListEditor(s.usageInstructions, (v) => set(p("usageInstructions"), v),
                t("INSTRUCTION_PLACEHOLDER", { defaultValue: "e.g. Greet the caller by $clientName$ in your first reply." }))}
            </div>

            <div>
              <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => removeSkill(key)}>
                {t("REMOVE_CONNECTOR", { defaultValue: "Remove this connector" })}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Section "Custom CRM / API" : liste dynamique de connecteurs + ajout.
  const renderCustomConnectors = () => {
    const connectorKeys = Object.keys(sk).filter((k) => sk[k] && typeof sk[k] === "object" && sk[k].dataQuery);
    return (
      <div style={{ marginTop: 16 }}>
        <Text weight="semibold" style={{ display: "block" }}>
          {t("CUSTOM_CONNECTORS", { defaultValue: "Custom CRM / API connectors" })}
        </Text>
        <Text size={200} style={{ display: "block", color: "#605e5c", marginBottom: 8 }}>
          {t("CUSTOM_CONNECTORS_DESC", { defaultValue: "URL-based connectors like the CRM lookup. Read data when the call starts, or (soon) send update requests during/after the call. At runtime, the first enabled read connector provides answer-time context." })}
        </Text>
        {connectorKeys.length === 0 && (
          <Text size={200} style={{ display: "block", color: "#a19f9d", marginBottom: 8 }}>
            {t("NO_CONNECTORS", { defaultValue: "No custom connector yet." })}
          </Text>
        )}
        {connectorKeys.map((k) => renderConnectorCard(k))}
        <Button appearance="primary" icon={<Add20Filled />} style={{ marginTop: 8 }} onClick={addConnector}>
          {t("ADD_CONNECTOR", { defaultValue: "Add a custom connector" })}
        </Button>
      </div>
    );
  };

  const renderSkills = () => (
    <div className="agentai-form-section">
      <Text size={200} style={{ display: "block", color: "#605e5c", marginBottom: 8 }}>
        {t("SKILLS_HINT", { defaultValue: "Click a skill to expand it; only one opens at a time. Toggle each one on or off." })}
      </Text>
      {skillCard("userSearch", "Directory Lookup (search_user)", "Search for a user in the Microsoft Teams directory.")}
      {skillCard("userPresence", "Presence Check (get_user_presence)", "Check a user's real-time availability.", (s) => (
        <Checkbox label={t("ANNOUNCE_STATUS", { defaultValue: "Announce the status to the caller" })}
          checked={!!s.announceStatus} onChange={(e, d) => set("skills.userPresence.announceStatus", d.checked)} />
      ))}
      {skillCard("transferToUser", "Transfer to User (transfer_to_user)", "Transfer the call to a Teams user.", (s) => (
        <Checkbox label={t("REQUIRE_CONFIRMATION", { defaultValue: "Require verbal confirmation before action" })}
          checked={!!s.requireConfirmation} onChange={(e, d) => set("skills.transferToUser.requireConfirmation", d.checked)} />
      ))}
      {skillCard("transferToPhone", "Transfer to Phone (transfer_to_phone)", "Transfer the call to an external phone number.", (s) => (
        <Checkbox label={t("REQUIRE_CONFIRMATION", { defaultValue: "Require verbal confirmation before action" })}
          checked={!!s.requireConfirmation} onChange={(e, d) => set("skills.transferToPhone.requireConfirmation", d.checked)} />
      ))}
      {skillCard("hangupCall", "Hangup (hangup_call)", "Always enabled. Ends the call after saying the farewell.", (s) => (
        <Field label={t("FAREWELL_MESSAGE", { defaultValue: "Farewell message" })} style={{ marginTop: 6 }}>
          <Input value={s.farewellMessage || ""} onChange={(e, d) => set("skills.hangupCall.farewellMessage", d.value)} />
        </Field>
      ))}
      {skillCard("leaveMessage", "Leave a Message (leave_message)", "Take a message for an unavailable user and forward it via Teams.", (s) => (
        <div style={{ marginTop: 6 }}>
          <Label weight="semibold">{t("INFO_TO_CAPTURE", { defaultValue: "Information to capture" })}</Label>
          {[["caller_name", "Caller name"], ["phone_number", "Phone number"], ["message", "Message content"]].map(([val, lbl]) => {
            const cur = s.captureInfo || [];
            return (
              <Checkbox key={val} label={lbl} checked={cur.includes(val)}
                onChange={(e, d) => set("skills.leaveMessage.captureInfo", d.checked ? [...cur, val] : cur.filter((x) => x !== val))} />
            );
          })}
        </div>
      ))}
      {renderCustomConnectors()}
    </div>
  );

  const setRoute = (idx, path, value) => {
    const routes = (cq.routes || []).map((r, i) => i === idx ? setIn(r, path, value) : r);
    set("skills.callQualification.routes", routes);
  };
  const toggleRoute = (idx) => setExpandedRoutes((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // Dropdown de queue : affiche le NOM, stocke l'id derrière (+ le nom pour l'affichage).
  const renderQueueDropdown = (queueId, onPick, placeholder) => (
    <Dropdown
      placeholder={placeholder || t("SELECT_QUEUE", { defaultValue: "Select a queue" })}
      value={queueNameById[queueId] || ""}
      selectedOptions={queueId ? [queueId] : []}
      onOptionSelect={(e, d) => onPick(d.optionValue, queueNameById[d.optionValue] || "")}
    >
      {queueOptions.map((q) => <Option key={q.id} value={q.id} text={q.name}>{q.name}</Option>)}
    </Dropdown>
  );

  const renderKeywordList = (route, idx, lang) => {
    const kws = route.keywords?.[lang] || [];
    return (
      <div style={{ marginTop: 4 }}>
        <Label size="small">{lang.toUpperCase()}</Label>
        {kws.map((kw, ki) => (
          <div key={ki} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
            <Input style={{ flex: 1 }} value={kw}
              onChange={(e, d) => setRoute(idx, ["keywords", lang], kws.map((k, j) => j === ki ? d.value : k))} />
            <Button appearance="subtle" icon={<Delete20Regular />}
              onClick={() => setRoute(idx, ["keywords", lang], kws.filter((_, j) => j !== ki))} />
          </div>
        ))}
        <Button size="small" appearance="subtle" icon={<Add20Filled />} style={{ marginTop: 4 }}
          onClick={() => setRoute(idx, ["keywords", lang], [...kws, ""])}>
          {t("ADD", { defaultValue: "Add" })}
        </Button>
      </div>
    );
  };

  const renderRouting = () => (
    <div className="agentai-form-section">
      <Label weight="semibold" style={{ display: "block" }}>
        {t("QUALIFICATION_STRATEGY", { defaultValue: "Qualification strategy" })}
      </Label>
      <Field label={t("STRATEGY", { defaultValue: "Strategy" })}>
        <Dropdown
          value={(QUALIFICATION_STRATEGIES.find((s) => s.value === cq.strategy)?.label) || cq.strategy || ""}
          selectedOptions={cq.strategy ? [cq.strategy] : []}
          onOptionSelect={(e, d) => set("skills.callQualification.strategy", d.optionValue)}>
          {QUALIFICATION_STRATEGIES.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
        </Dropdown>
      </Field>
      <Checkbox label={t("CLARIFY_BEFORE_ROUTING", { defaultValue: "Ask a clarifying question before routing when intent is unclear" })}
        checked={!!cq.clarifyBeforeRouting} onChange={(e, d) => set("skills.callQualification.clarifyBeforeRouting", d.checked)} />
      <Field label={t("MAX_CLARIFYING", { defaultValue: "Maximum clarifying questions" })}>
        <Input type="number" value={String(cq.maxClarifyingQuestions ?? 1)}
          onChange={(e, d) => set("skills.callQualification.maxClarifyingQuestions", Number(d.value) || 0)} />
      </Field>

      <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
        {t("ROUTES", { defaultValue: "Routes" })}
      </Label>
      {(cq.routes || []).map((route, idx) => {
        const open = !!expandedRoutes[idx];
        const title = route.queueName || queueNameById[route.queueId] || t("NEW_ROUTE", { defaultValue: "New route" });
        return (
          <div className="agentai-skill-card" key={idx} style={{ padding: 0 }}>
            {/* En-tête repliable */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", cursor: "pointer" }}
              onClick={() => toggleRoute(idx)}>
              {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
              <Text weight="semibold" style={{ flex: 1 }}>{title}</Text>
              <Button appearance="subtle" icon={<Delete20Regular />}
                onClick={(e) => { e.stopPropagation(); set("skills.callQualification.routes", (cq.routes || []).filter((_, i) => i !== idx)); }} />
            </div>
            {/* Corps (uniquement si déplié) */}
            {open && (
              <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                <Field label={t("QUEUE", { defaultValue: "Queue" })}>
                  {renderQueueDropdown(route.queueId, (id, name) => {
                    // queueId + queueName en UN SEUL update : deux setRoute consécutifs liraient
                    // le même cq.routes figé et le 2e écraserait le 1er (queueId perdu).
                    const routes = (cq.routes || []).map((r, i) => i === idx ? { ...r, queueId: id, queueName: name } : r);
                    set("skills.callQualification.routes", routes);
                  })}
                </Field>
                <Field label={t("INTENT", { defaultValue: "Intent (internal key)" })}>
                  <Input value={route.intent || ""} placeholder="e.g. support"
                    onChange={(e, d) => setRoute(idx, "intent", d.value)} />
                </Field>
                <Field label={t("DESCRIPTION", { defaultValue: "Description (when to use this route)" })}>
                  <Textarea rows={2} value={route.description || ""}
                    onChange={(e, d) => setRoute(idx, "description", d.value)} />
                </Field>
                <Label weight="semibold">{t("KEYWORDS", { defaultValue: "Keywords" })}</Label>
                {renderKeywordList(route, idx, "en")}
                {renderKeywordList(route, idx, "fr")}
                <Label weight="semibold">{t("EXAMPLES", { defaultValue: "Example phrases" })}</Label>
                {renderListEditor(route.examples, (next) => setRoute(idx, "examples", next),
                  t("EXAMPLE_PH", { defaultValue: "e.g. I have a problem with my invoice" }))}
                <Field label={t("ANNOUNCEMENT", { defaultValue: "Announcement (spoken before transfer)" })}>
                  <Input value={route.announcement?.en || ""} placeholder="EN" onChange={(e, d) => setRoute(idx, ["announcement", "en"], d.value)} />
                </Field>
                <Field>
                  <Input value={route.announcement?.fr || ""} placeholder="FR" onChange={(e, d) => setRoute(idx, ["announcement", "fr"], d.value)} />
                </Field>
              </div>
            )}
          </div>
        );
      })}
      <Button appearance="secondary" icon={<Add20Filled />}
        onClick={() => {
          const next = (cq.routes || []).length;
          set("skills.callQualification.routes", [...(cq.routes || []), { queueId: "", queueName: "", keywords: { en: [], fr: [] }, announcement: { en: "", fr: "" } }]);
          setExpandedRoutes((prev) => ({ ...prev, [next]: true })); // nouvelle route dépliée
        }}>
        {t("ADD_ROUTE", { defaultValue: "Add route" })}
      </Button>

      <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>{t("FALLBACK", { defaultValue: "Fallback" })}</Label>
      <Field label={t("FALLBACK_QUEUE", { defaultValue: "Fallback queue (when intent stays unclear)" })}>
        {renderQueueDropdown(cq.fallback?.ifStillUnclear?.queueId, (id) =>
          set("skills.callQualification.fallback.ifStillUnclear", { ...(cq.fallback?.ifStillUnclear || {}), queueId: id }))}
      </Field>
      <Field label={t("DISAMBIGUATION", { defaultValue: "Disambiguation message" })}>
        <Textarea rows={2} value={cq.fallback?.disambiguationMessage?.en || ""} placeholder="EN"
          onChange={(e, d) => set("skills.callQualification.fallback.disambiguationMessage", { ...(cq.fallback?.disambiguationMessage || {}), en: d.value })} />
      </Field>
      <Field>
        <Textarea rows={2} value={cq.fallback?.disambiguationMessage?.fr || ""} placeholder="FR"
          onChange={(e, d) => set("skills.callQualification.fallback.disambiguationMessage", { ...(cq.fallback?.disambiguationMessage || {}), fr: d.value })} />
      </Field>

      <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
        {t("QUALIFICATION_RULES", { defaultValue: "Qualification rules" })}
      </Label>
      <Text size={200} style={{ color: "#605e5c", display: "block" }}>
        {t("QUALIFICATION_RULES_HINT", { defaultValue: "Ordered rules the agent applies to decide how and where to route the caller." })}
      </Text>
      {renderListEditor(cq.rules, (next) => set("skills.callQualification.rules", next),
        t("QUALIFICATION_RULE_PH", { defaultValue: "e.g. Announce the queue in the caller's language before transferring" }))}
    </div>
  );

  // Mode UI : la sélection explicite (kbModeOverride) prime ; à défaut on déduit du doc
  // (url → URL ; sinon rawText présent → Markdown ; sinon → Q&A JSON). Sans override, choisir
  // "Markdown" ne tiendrait pas tant que rawText est vide → d'où l'override.
  // (Q&A et Markdown utilisent tous deux type:"inline" au runtime ; rawText prime sur data.)
  const derivedKbMode = kb.type === "url" ? "url" : (kb.rawText ? "markdown" : "qa");
  const kbMode = kbModeOverride || derivedKbMode;
  const setKbMode = (m) => {
    setKbModeOverride(m); // mémorise le choix UI même si rawText est encore vide
    if (m === "url") {
      set("knowledgeBase.type", "url");
    } else {
      set("knowledgeBase.type", "inline");
      if (m === "qa") set("knowledgeBase.rawText", ""); // repasse en Q&A → on vide le markdown
    }
  };
  const KB_MODE_LABELS = { qa: "Q&A (JSON)", markdown: "Markdown / Text (inline)", url: "External URL" };

  const renderKnowledge = () => (
    <div className="agentai-form-section">
      <Switch checked={!!kb.enabled} label={t("KB_ENABLED", { defaultValue: "Knowledge base enabled" })}
        onChange={(e, d) => set("knowledgeBase.enabled", d.checked)} />
      <Field label={t("KB_NAME", { defaultValue: "Name" })}>
        <Input value={kb.name || ""} onChange={(e, d) => set("knowledgeBase.name", d.value)} />
      </Field>
      <Field label={t("KB_PRIMARY_LANGUAGE", { defaultValue: "Primary language" })}>
        <Dropdown value={kb.language || ""} selectedOptions={kb.language ? [kb.language] : []}
          onOptionSelect={(e, d) => set("knowledgeBase.language", d.optionValue)}>
          <Option value="en">en</Option>
          <Option value="fr">fr</Option>
        </Dropdown>
      </Field>

      <Field label={t("KB_FORMAT", { defaultValue: "Source format" })}>
        <Dropdown value={KB_MODE_LABELS[kbMode]} selectedOptions={[kbMode]}
          onOptionSelect={(e, d) => setKbMode(d.optionValue)}>
          <Option value="qa" text={KB_MODE_LABELS.qa}>{KB_MODE_LABELS.qa}</Option>
          <Option value="markdown" text={KB_MODE_LABELS.markdown}>{KB_MODE_LABELS.markdown}</Option>
          <Option value="url" text={KB_MODE_LABELS.url}>{KB_MODE_LABELS.url}</Option>
        </Dropdown>
      </Field>

      <Field label={t("KB_USAGE", { defaultValue: "Usage instructions" })}>
        <Textarea rows={3} value={kb.usageInstructions || ""} onChange={(e, d) => set("knowledgeBase.usageInstructions", d.value)} />
      </Field>
      <Field label={t("KB_FALLBACK", { defaultValue: "Fallback message" })}>
        <Textarea rows={2} value={kb.fallbackMessage || ""} onChange={(e, d) => set("knowledgeBase.fallbackMessage", d.value)} />
      </Field>

      {/* Champ spécifique au format choisi */}
      {kbMode === "qa" && (
        <Field label={t("KB_QA", { defaultValue: "Q&A data (JSON)" })}>
          <Textarea rows={8}
            value={Array.isArray(kb.data) && kb.data.length ? JSON.stringify(kb.data, null, 2) : ""}
            placeholder={'[{ "question": "…", "answer": "…" }]'}
            onChange={(e, d) => {
              const v = d.value.trim();
              if (!v) { set("knowledgeBase.data", []); return; }
              try { set("knowledgeBase.data", JSON.parse(v)); } catch (_) { /* JSON invalide en cours de saisie */ }
            }} />
        </Field>
      )}

      {kbMode === "markdown" && (() => {
        const len = (kb.rawText || "").length;
        const KB_MAX = 8000; // ~3 pages — doit correspondre au cap runtime (AgentConfigLoader)
        const over = len > KB_MAX;
        return (
          <Field
            label={t("KB_MARKDOWN", { defaultValue: "Markdown / Text content" })}
            validationState={over ? "warning" : "none"}
            validationMessage={
              over
                ? t("KB_MARKDOWN_OVER", { len, max: KB_MAX, defaultValue: `${len} / ${KB_MAX} characters — beyond ~3 pages the text is TRUNCATED to ${KB_MAX} at runtime. Shorten it.` })
                : t("KB_MARKDOWN_HINT", { len, max: KB_MAX, defaultValue: `Injected as-is into the agent context. Max ~3 pages (${KB_MAX} characters) — ${len} used.` })
            }>
            <Textarea rows={14} resize="vertical" value={kb.rawText || ""}
              placeholder={"## FAQ\n\n### Suivi de colis\nUtilisez votre numéro d'expédition…\n\n### Délais\nStandard 24-48h."}
              onChange={(e, d) => set("knowledgeBase.rawText", d.value)} />
          </Field>
        );
      })()}

      {kbMode === "url" && (
        <>
          <Field label={t("KB_URL", { defaultValue: "URL" })}
            hint={t("KB_URL_HINT", { defaultValue: "Blob/CDN URL to a .md/.txt (text), a JSON Q&A file, or a web page (html)." })}>
            <Input value={kb.url || ""} placeholder="https://…/faq.md" onChange={(e, d) => set("knowledgeBase.url", d.value)} />
          </Field>
          <Field label={t("KB_URL_FORMAT", { defaultValue: "URL content format" })}>
            <Dropdown value={kb.format || "text"} selectedOptions={[kb.format || "text"]}
              onOptionSelect={(e, d) => set("knowledgeBase.format", d.optionValue)}>
              <Option value="text">text / markdown</Option>
              <Option value="json">json (Q&amp;A)</Option>
              <Option value="html">html (web page)</Option>
            </Dropdown>
          </Field>
          <Field label={t("KB_URL_TIMEOUT", { defaultValue: "Fetch timeout (s)" })}>
            <Input type="number" style={{ maxWidth: 100 }} value={String(kb.timeoutSec ?? 10)}
              onChange={(e, d) => set("knowledgeBase.timeoutSec", Number(d.value) || 0)} />
          </Field>
        </>
      )}
    </div>
  );

  const renderSecurity = () => (
    <div className="agentai-form-section">
      <Label weight="semibold">{t("SECURITY_RULES", { defaultValue: "Security rules" })}</Label>
      {(agentAI.securityRules || []).map((rule, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
          <Input style={{ flex: 1 }} value={rule}
            onChange={(e, d) => set("securityRules", (agentAI.securityRules || []).map((r, idx) => idx === i ? d.value : r))} />
          <Button appearance="subtle" icon={<Delete20Regular />}
            onClick={() => set("securityRules", (agentAI.securityRules || []).filter((_, idx) => idx !== i))} />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
        <Input style={{ flex: 1 }} value={newSecurityRule} placeholder={t("ADD_SECURITY_RULE", { defaultValue: "Add a security rule" })}
          onChange={(e, d) => setNewSecurityRule(d.value)} />
        <Button appearance="primary" icon={<Add20Filled />} disabled={!newSecurityRule.trim()}
          onClick={() => { set("securityRules", [...(agentAI.securityRules || []), newSecurityRule.trim()]); setNewSecurityRule(""); }}>
          {t("ADD", { defaultValue: "Add" })}
        </Button>
      </div>

      <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>{t("ERROR_HANDLING", { defaultValue: "Error handling" })}</Label>
      <Field label={t("MAX_RETRIES", { defaultValue: "Max retries" })}>
        <Input type="number" value={String(eh.maxRetries ?? 3)} onChange={(e, d) => set("errorHandling.maxRetries", Number(d.value) || 0)} />
      </Field>
      <Checkbox label={t("FALLBACK_TO_HUMAN", { defaultValue: "Fallback to human" })}
        checked={!!eh.fallbackToHuman} onChange={(e, d) => set("errorHandling.fallbackToHuman", d.checked)} />
      <Field label={t("FALLBACK_QUEUE", { defaultValue: "Fallback queue" })}>
        {renderQueueDropdown(eh.fallbackQueueId, (id) => set("errorHandling.fallbackQueueId", id))}
      </Field>
      {[["userNotFound", "User not found"], ["transferFailed", "Transfer failed"], ["technicalError", "Technical error"], ["timeout", "Timeout"]].map(([k, lbl]) => (
        <Field key={k} label={lbl}>
          <Input value={eh.errorMessages?.[k] || ""} onChange={(e, d) => set(["errorHandling", "errorMessages", k], d.value)} />
        </Field>
      ))}
    </div>
  );

  const renderAdvanced = () => {
    const an = agentAI.analytics || {};
    const delays = agentAI.advanced?.delays || {};
    const vad = agentAI.advanced?.vad || {};
    const analyticsToggles = [
      ["trackConversations", t("TRACK_CONVERSATIONS", { defaultValue: "Track conversations" })],
      ["trackToolsUsage", t("TRACK_TOOLS", { defaultValue: "Track tools usage" })],
      ["trackLanguageDetection", t("TRACK_LANGUAGE", { defaultValue: "Track language detection" })],
      ["trackQualificationRoute", t("TRACK_QUALIFICATION", { defaultValue: "Track qualification route" })],
      ["trackSentiment", t("TRACK_SENTIMENT", { defaultValue: "Track sentiment" })],
    ];
    const delayFields = [
      ["before_transfer_ms", t("DELAY_TRANSFER", { defaultValue: "Delay before transfer (ms)" })],
      ["before_hangup_ms", t("DELAY_HANGUP", { defaultValue: "Delay before hangup (ms)" })],
      ["audio_commit_ms", t("DELAY_AUDIO_COMMIT", { defaultValue: "Audio commit delay (ms)" })],
      ["audio_delay_barge_in_ms", t("DELAY_BARGE_IN", { defaultValue: "Barge-in audio delay (ms)" })],
    ];
    return (
      <div className="agentai-form-section">
        <Label weight="semibold" style={{ display: "block" }}>
          {t("GENERAL_CONFIG", { defaultValue: "General configuration" })}
        </Label>
        <Switch checked={!!agentAI.enabled} label={t("AGENT_ENABLED", { defaultValue: "Agent enabled" })}
          onChange={(e, d) => set("enabled", d.checked)} />
        <Field label={t("REGION", { defaultValue: "Region" })}>
          <Dropdown
            value={(REGION_OPTIONS.find((r) => r.value === agentAI.region)?.label) || agentAI.region || ""}
            selectedOptions={agentAI.region ? [agentAI.region] : []}
            onOptionSelect={(e, d) => set("region", d.optionValue)}>
            {REGION_OPTIONS.map((r) => <Option key={r.value} value={r.value}>{r.label}</Option>)}
          </Dropdown>
        </Field>
        <Field label={t("DATA_LOCATION", { defaultValue: "Data location" })}>
          <Input value={agentAI.dataLocation || ""} onChange={(e, d) => set("dataLocation", d.value)} />
        </Field>
        <Field label={t("TIMEZONE", { defaultValue: "Time zone" })}
          hint={t("TIMEZONE_HINT", { defaultValue: "IANA name, e.g. Europe/Amsterdam" })}>
          <Input value={agentAI.timeZone || ""} onChange={(e, d) => set("timeZone", d.value)} />
        </Field>

        <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
          {t("ANALYTICS", { defaultValue: "Analytics" })}
        </Label>
        {analyticsToggles.map(([key, lbl]) => (
          <Checkbox key={key} label={lbl} checked={!!an[key]}
            onChange={(e, d) => set(["analytics", key], d.checked)} />
        ))}
        <Field label={t("LOG_LEVEL", { defaultValue: "Log level" })}>
          <Dropdown value={an.logLevel || ""} selectedOptions={an.logLevel ? [an.logLevel] : []}
            onOptionSelect={(e, d) => set(["analytics", "logLevel"], d.optionValue)}>
            {LOG_LEVEL_OPTIONS.map((l) => <Option key={l} value={l}>{l}</Option>)}
          </Dropdown>
        </Field>

        <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
          {t("ADVANCED_DELAYS", { defaultValue: "Advanced delays" })}
        </Label>
        <Text size={200} style={{ color: "#605e5c", display: "block" }}>
          {t("ADVANCED_DELAYS_HINT", { defaultValue: "Fine-tune realtime timing. Leave defaults unless you know what you are doing." })}
        </Text>
        {delayFields.map(([key, lbl]) => (
          <Field key={key} label={lbl}>
            <Input type="number" value={String(delays[key] ?? "")}
              onChange={(e, d) => set(["advanced", "delays", key], Number(d.value) || 0)} />
          </Field>
        ))}

        <Label weight="semibold" style={{ marginTop: 16, display: "block" }}>
          {t("VAD_SECTION", { defaultValue: "Voice activity detection (noise sensitivity)" })}
        </Label>
        <Text size={200} style={{ color: "#605e5c", display: "block" }}>
          {t("VAD_HINT", { defaultValue: "Higher threshold = the agent is less easily cut off by background noise (but may miss a soft-spoken caller). Default 0.5. Leave empty to use the server default." })}
        </Text>
        <Field label={t("VAD_THRESHOLD", { defaultValue: "Interruption threshold (0.3 – 0.9)" })}>
          <Input type="number" step="0.05" min="0" max="1"
            value={String(vad.threshold ?? 0.5)}
            onChange={(e, d) => set(["advanced", "vad", "threshold"], Number(d.value))} />
        </Field>
        <Field label={t("VAD_SILENCE", { defaultValue: "Silence before end of turn (ms)" })}>
          <Input type="number" value={String(vad.silence_duration_ms ?? 500)}
            onChange={(e, d) => set(["advanced", "vad", "silence_duration_ms"], Number(d.value) || 0)} />
        </Field>
      </div>
    );
  };

  const renderTab = () => {
    switch (selectedTab) {
      case "identity": return renderIdentity();
      case "voice": return renderVoice();
      case "personality": return renderPersonality();
      case "greeting": return renderGreeting();
      case "skills": return renderSkills();
      case "routing": return renderRouting();
      case "knowledge": return renderKnowledge();
      case "security": return renderSecurity();
      case "advanced": return renderAdvanced();
      default: return null;
    }
  };

  return (
    <div className="agentai-container">
      <div className="agentai-header-wrapper">
        <TabList selectedValue={selectedTab} onTabSelect={(_, d) => setSelectedTab(d.value)} className="agentai-tab-list">
          {TABS.map((tab) => <Tab key={tab.value} value={tab.value}>{tab.label}</Tab>)}
        </TabList>
        <div className="agentai-actions-bar">
          {agentAI?.displayName && <p className="agentai-display-name">{agentAI.displayName}</p>}
          <div className="agentai-action-buttons">
            <Button icon={<Dismiss20Regular />} appearance="secondary" onClick={() => navigate("/iaagent")}>
              {t("CANCEL", { defaultValue: "Cancel" })}
            </Button>
            <Button icon={<Save20Regular />} appearance="primary" onClick={handleSave} disabled={saving}>
              {saving ? t("SAVING", { defaultValue: "Saving…" }) : t("SAVE", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      </div>
      <div className="agentai-tab-content">{renderTab()}</div>
    </div>
  );
};

export default AgentAIFormComponent;
